import type { AuthContextValue as SharedAuthContextValue } from '@frontend-shared/auth/types'
import type { PartialExchangeAvailability } from '@frontend-shared/exchanges/types'

export interface PortalCompanyInfo {
  id: number
  name?: string | null
  type?: string | null
  logoDataUrl?: string | null
  masterUserId?: number | null
  [key: string]: unknown
}

export interface PortalUserProfileSettings {
  firstName?: string
  lastName?: string
  phone?: string
  language?: string
  /** Preferowany motyw portalu: 'dark' (domyślny) lub 'light'. */
  theme?: string
  [key: string]: unknown
}

export interface PortalUserSettings {
  dispatcherOps?: {
    demandMap?: {
      vehicleType?: string | null
      [key: string]: unknown
    }
    [key: string]: unknown
  }
  exchangeAvailability?: PartialExchangeAvailability
  timocomFreightSearchEnabled?: boolean
  timocomVehicleSearchEnabled?: boolean
  user?: PortalUserProfileSettings
  /** Per-user włączane moduły portalu (poza company masterem, który ma wszystko). */
  modules?: {
    statistics?: boolean
    [key: string]: unknown
  }
  /** Auto-zapisywane filtry tabel unified-offers per tabela (useUnifiedOffersStickyFilters);
   *  klucze multi-wyboru (shipper/vehicle) jako tablice stringów, tekstowe jako stringi. */
  tableFilters?: Record<
    string,
    { v: number; updatedAt?: string; filters?: Record<string, string | string[]> }
  >
  /** Preferencje tabel Filtrów v2 per ekran (components/table-filters): filtry, sort, widoki. */
  tableViews?: Record<string, unknown>
  [key: string]: unknown
}

export interface PortalProfile {
  id: number
  username: string
  displayName?: string | null
  config?: Record<string, unknown>
  settings?: PortalUserSettings
  company?: PortalCompanyInfo | null
  isCompanyMaster?: boolean
  companyMaster?: boolean
  integrations?: Record<string, boolean>
  exchangeAvailability?: PartialExchangeAvailability
  /** 3 najczęściej używane przez użytkownika wagi (z historii publikacji), do chipów w formularzu Smart Spot. */
  frequentWeights?: string[]
  /** Najczęściej używane przez użytkownika typy pojazdu — kolejność chipów w „Dodaj fracht”. */
  frequentVehicleTypes?: string[]
  /** Najczęściej używane przez użytkownika typy nadwozia — zawartość chipów w „Dodaj fracht”. */
  frequentBodyTypes?: string[]
  /** Read-only flagi funkcji per firma z /api/me (np. nicoCascade) — poza settings, nie do edycji przez PATCH. */
  features?: Record<string, boolean>
}

export interface PortalAuthResponse {
  token: string
  profile: PortalProfile
}

export type AuthContextValue = SharedAuthContextValue<PortalProfile>

export function isCompanyMasterProfile(profile: PortalProfile | null | undefined): boolean {
  if (!profile) {
    return false
  }
  if (typeof profile.isCompanyMaster === 'boolean') {
    return profile.isCompanyMaster
  }
  if (typeof profile.companyMaster === 'boolean') {
    return profile.companyMaster
  }
  const masterUserId = profile.company?.masterUserId
  return typeof masterUserId === 'number' && Number.isFinite(masterUserId) && masterUserId === profile.id
}

/**
 * Dostęp do modułu Statystyki: company master ma zawsze; zwykły user tylko gdy admin/master
 * jawnie włączył flagę user_settings.modules.statistics.
 */
export function hasStatisticsModuleAccess(profile: PortalProfile | null | undefined): boolean {
  if (!profile) {
    return false
  }
  if (isCompanyMasterProfile(profile)) {
    return true
  }
  return profile.settings?.modules?.statistics === true
}
